import React, { useContext, useEffect } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Link from 'antd/es/typography/Link';
import moment from 'moment';

import { Layout, Typography, theme } from 'antd';

import { selectDarkMode } from '../../../../userBrowserSettings/store/browserSettings.selectors';
import {
    setContentOptimizerForUpdate,
    setContentOptimizerTabKey,
    updateContentOptimizerMainTab,
} from '../mainPage/store/contentOptimizerMain.actions';
import { selectContentOptimizerTabKey } from '../mainPage/store/contentOptimizerMain.selectors';
import { selectContentOptimizerViewOnlyMode } from '../viewOnly/store/viewOnlySettings.selectors';
import { clearContentBriefsSettings } from './content/briefTab/content/tabsContent/store/contentBriefsOutline.actions';
import {
    clearContentOptimizerContent,
    setOptimizerLoading,
    updateCurrentContentOptimizer,
} from './store/contentOptimizerContent.actions';
import { selectCurrentContentOptimizer } from './store/contentOptimizerContent.selectors';

import { ContentOptimizerContext } from '../../../hocs/ContentOptimizerProvider';

import { contentOptimizerLSTabKey } from '../../../../constants/contentOptimizer';
import { contentOptimizerMainTabs } from '../../../../constants/contentOptimizerMainTabs';
import { contentOptimizerPathes } from '../../../../constants/queryPathes';
import { statusCodes } from '../../../../constants/statusCodes';

import { axiosAPI } from '../../../../utils/axiosAPI';
import { getAxiosHeaders } from '../../../../utils/helpers/getAxiosHeaders';
import { openBasicErrorNotification } from '../../../../utils/helpers/openBasicErrorNotification';
import { getThemeColorClass } from '../../../../utils/helpers/themeHelpers';
import { useOrganizationSlug } from '../../../../utils/hooks/useOrganizationSlug';
import {
    useTrackProgressAIBrief,
    useTrackProgressAIContent,
    useTrackProgressAIOutline,
} from '../../../../utils/hooks/aiProgressTracking';

import CustomHeader from '../../../common/header/Header';
import CommonPageLayout from '../../../common/layout/CommonPageLayout';
import NotificationCard from '../../../common/notifications/NotificationCard';
import { clearContentOptimizerEditor } from '../../../common/tiptapEditor/store/tiptapEditor.actions';

import ContentOptimizerHeaderContent from './content/ContentOptimizerHeaderContent';
import ContentOptimizerTopContent from './content/ContentOptimizerTopContent';
import FireworksContainer from './content/FireworksContainer';
import ContentOptimizerPageTabContainer from './ContentOptimizerPageTabContainer';

import { contentOptimizerContentTabValues } from './contentOptimizerContentConstants';

import './ContentOptimizerContentPage.scss';

const { Content } = Layout;

const classes = {
    root: 'content-optimizer-content-page',
    content: 'content-optimizer-content',
    contentDark: 'container-dark',
    warningNotification: 'optimizer-date-warning-notification',
    infoNotification: 'optimizer-info-notification',
    contentBelowWarningContainer: 'optimizer-below-warning-content-container',
    title: 'title',
};

const customOptimizerPageLayoutStyle = { overflowY: 'hidden' };

const ContentOptimizerContentPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const contentOptimizerId = parseInt(useContext(ContentOptimizerContext));

    const darkMode = useSelector(selectDarkMode);
    const viewOnlyMode = useSelector(selectContentOptimizerViewOnlyMode);
    const currentContentOptimizer = useSelector(selectCurrentContentOptimizer);
    const currentTabKey = useSelector(selectContentOptimizerTabKey);

    const organizationSlug = useOrganizationSlug();

    /**
     * Track AI progress in high level component in order to redirect user to the right tab
     * after AI step is completed.
     */
    useTrackProgressAIOutline();
    useTrackProgressAIBrief();
    useTrackProgressAIContent(contentOptimizerId);

    const {
        token: { colorBgContainer },
    } = theme.useToken();

    useEffect(() => {
        let key;

        if (viewOnlyMode) {
            key = contentOptimizerContentTabValues.optimizer.value;
            localStorage.setItem(
                contentOptimizerLSTabKey,
                contentOptimizerContentTabValues.optimizer.value
            );
        }

        const coTabKey =
            localStorage.getItem(contentOptimizerLSTabKey) ||
            contentOptimizerContentTabValues.brief.value;

        localStorage.setItem(contentOptimizerLSTabKey, coTabKey);

        key = coTabKey;

        dispatch(setContentOptimizerTabKey(key));
    }, [viewOnlyMode, dispatch]);

    useEffect(() => {
        if (contentOptimizerId && organizationSlug) {
            dispatch(setOptimizerLoading(true));

            axiosAPI
                .get(`${contentOptimizerPathes.getContentOptimizers}/${contentOptimizerId}`, {
                    ...getAxiosHeaders(),
                    params: { slug: organizationSlug },
                })
                .then((result) => {
                    if (result?.status === statusCodes.success) {
                        dispatch(updateCurrentContentOptimizer(result?.data));
                    }
                })
                .catch(() => {
                    openBasicErrorNotification();
                })
                .finally(() => {
                    dispatch(setOptimizerLoading(false));
                });
        }
    }, [contentOptimizerId, organizationSlug, dispatch]);

    useEffect(() => {
        return () => {
            batch(() => {
                dispatch(clearContentBriefsSettings());
                dispatch(clearContentOptimizerContent());
                dispatch(clearContentOptimizerEditor());
            });
        };
    }, [dispatch]);

    const handleNewSearchClick = () => {
        batch(() => {
            dispatch(setContentOptimizerForUpdate(currentContentOptimizer.content_brief.keywords));
            dispatch(updateContentOptimizerMainTab(contentOptimizerMainTabs.single.key));
        });
        navigate(`/${organizationSlug}/optimizer`);
    };

    const notificationCardContent = (
        <Typography className={`${classes.title} ${getThemeColorClass(darkMode)}`}>
            {`This Optimizer was created on ${moment(currentContentOptimizer?.created_at).format(
                'MMM DD, YYYY'
            )} and may not reflect the most recent NLP and competitor data for "${
                currentContentOptimizer?.content_brief.keywords
            }". You should consider `}
            <Link onClick={handleNewSearchClick}>running a new Optimizer</Link> for fresh data.
        </Typography>
    );

    const openContactSupportModal = () => {
        window.Beacon && window.Beacon('open');
    };

    const notificationOldEditorContent = (
        <Typography className={`${classes.title} ${getThemeColorClass(darkMode)}`}>
            We’re pleased to inform you that we have migrated to a new Optimizer editor. The new
            editor is much more stable and less buggy than our previous version. However, you may
            need to fix some minor structural changes in your article below that was created with
            our old editor. If you have any issues, please{' '}
            <Link onClick={openContactSupportModal}>Contact Support</Link>.
        </Typography>
    );

    const notificationGlobalError = (
        <Typography className={`${classes.title} ${getThemeColorClass(darkMode)}`}>
            We’re currently experiencing technical difficulties with our Optimizer results. Our team
            is actively working to fix the issue as quickly as possible. In the meantime, please
            refrain from running additional Optimizers. We apologize for the inconvenience and thank
            you for your patience.
        </Typography>
    );

    const isTheOptimizerExpired = (createdAt) => {
        const threeMonthsAgo = moment().subtract(90, 'days');

        return threeMonthsAgo > moment(createdAt);
    };

    return (
        <>
            <CustomHeader
                extraBreadcrumbs={[<ContentOptimizerHeaderContent />]}
                // TODO: — Hide all “How To Use” buttons (temporary)
                //showUseSetting
                showShareSetting={!viewOnlyMode}
                showProjectSetting={!viewOnlyMode}
            />
            <CommonPageLayout customStyle={customOptimizerPageLayoutStyle}>
                {isTheOptimizerExpired(currentContentOptimizer?.created_at) && (
                    <div className={`${classes.warningNotification}`}>
                        <NotificationCard content={notificationCardContent} />
                    </div>
                )}

                <div
                    className={`${classes.infoNotification} d-none`}
                    data-editor-notification='true'
                >
                    <NotificationCard content={notificationOldEditorContent} />
                </div>
                {currentContentOptimizer?.show_global_optimizer_error && (
                    <div className={`${classes.warningNotification}`}>
                        <NotificationCard content={notificationGlobalError} />
                    </div>
                )}

                <Content
                    style={{
                        background: colorBgContainer,
                    }}
                    className={`${classes.content} ${
                        isTheOptimizerExpired(currentContentOptimizer?.created_at)
                            ? classes.contentBelowWarningContainer
                            : ''
                    } ${darkMode ? `${classes.contentDark}` : ''}`}
                >
                    <ContentOptimizerTopContent />
                    <ContentOptimizerPageTabContainer
                        tabKey={currentTabKey}
                        viewOnlyMode={viewOnlyMode}
                        currentContentExists={!!currentContentOptimizer}
                    />
                </Content>
                {!!currentContentOptimizer && (
                    <FireworksContainer activeContentEditorTab={currentTabKey} />
                )}
            </CommonPageLayout>
        </>
    );
};

export default ContentOptimizerContentPage;
